// Instalace PWA (SPEC/PLAN 2.4): Chrome a Edge na Windows a Androidu posílají `beforeinstallprompt`,
// výzvu si uložíme a ukážeme vlastní tlačítko. iOS Safari událost nemá, tam zůstává ruční postup.
import { computed, ref } from 'vue'
import { isNative } from './native'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'wac-buddy.install-dismissed'

const deferred = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
const dismissed = ref(false)

export type InstallState = 'installed' | 'available' | 'ios' | 'unsupported'

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const nav = window.navigator as Navigator & { standalone?: boolean }
  return window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone === true
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

/** Zavolat jednou při startu aplikace (main.ts). */
export function initInstallPrompt(): void {
  if (typeof window === 'undefined') return
  installed.value = isStandalone()
  dismissed.value = readDismissed()
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred.value = e as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    deferred.value = null
  })
}

export const installState = computed<InstallState>(() => {
  // Nativní aplikace z obchodu je "nainstalovaná" vždy: žádná výzva ani návod k instalaci.
  if (isNative() || installed.value) return 'installed'
  if (deferred.value) return 'available'
  if (isIos()) return 'ios'
  return 'unsupported'
})

/** Banner na obrazovce Dnes: jen když jde instalovat a uživatel ho neodložil. */
export const showInstallBanner = computed(
  () => installState.value === 'available' && !dismissed.value,
)

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = deferred.value
  if (!ev) return 'unavailable'
  await ev.prompt()
  const { outcome } = await ev.userChoice
  if (outcome === 'accepted') installed.value = true
  deferred.value = null
  return outcome
}

export function dismissInstallBanner(): void {
  dismissed.value = true
  try {
    localStorage.setItem(DISMISS_KEY, '1')
  } catch {
    /* soukromý režim */
  }
}

/** Jen pro testy. */
export function _resetInstallState(): void {
  deferred.value = null
  installed.value = false
  dismissed.value = false
}
